import { VStack, HStack, Box } from '@terra/ui-styled-system';
import { Text } from '../../../../../components/styled/text';
import { Badge } from '../../../../../components/badge';
import { Globe02Icon, Link02Icon } from '@terra/ui-icons';
import type { ParsedEndpoint } from '../types';
import { Tooltip } from '../../../../../components/tooltip';
import type { OnOpenChatObject } from '../../hitl-types';
import { renderHttpMethodText } from '../../../../../utils/http-badge.utils';

const AUTH_LABEL = 'Auth';
const API_ENDPOINTS_LABEL = 'API Endpoints';

type EndpointsRendererProps = {
  endpoints: ParsedEndpoint[];
  rawContent?: string;
  maxHeight?: string;
  onOpenObject?: OnOpenChatObject;
};
type EndpointTooltipContentProps = {
  endpoint: ParsedEndpoint;
};

function EndpointTooltipContent({ endpoint }: EndpointTooltipContentProps) {
  return (
    <VStack gap={2} alignItems="flex-start" minW="200px" maxW="350px">
      <HStack gap={2} alignItems="center" width="100%">
        {endpoint.method ? (
          <Box flexShrink={0}>{renderHttpMethodText(endpoint.method)}</Box>
        ) : null}
        {(endpoint.path || endpoint.endpointUrl) && (
          <Text
            fontSize="xs"
            fontFamily="mono"
            color="fg.neutral.emphasized"
            wordBreak="break-all"
          >
            {endpoint.path || endpoint.endpointUrl}
          </Text>
        )}
      </HStack>

      {endpoint.endpointDescription && (
        <Box width="100%">
          <Text
            fontSize="xs"
            color="fg.neutral.emphasizedMuted"
            lineHeight="1.4"
          >
            {endpoint.endpointDescription}
          </Text>
        </Box>
      )}

      <HStack gap={2} flexWrap="wrap" width="100%">
        {endpoint.rank !== undefined && (
          <Badge size="sm" variant="emphasis">
            #{endpoint.rank}
          </Badge>
        )}
        {endpoint.score && (
          <Badge size="sm" variant="emphasis">
            {endpoint.score}
          </Badge>
        )}
        {endpoint.credentialId && (
          <Badge size="sm" variant="outline">
            {AUTH_LABEL}
          </Badge>
        )}
        {endpoint.endpointId && (
          <Text
            fontSize="2xs"
            color="fg.neutral.emphasizedMuted"
            fontFamily="mono"
          >
            {endpoint.endpointId.slice(0, 8)}...
          </Text>
        )}
      </HStack>
    </VStack>
  );
}

type EndpointItemProps = {
  endpoint: ParsedEndpoint;
  onOpenObject?: OnOpenChatObject;
};

function EndpointItem({ endpoint, onOpenObject }: EndpointItemProps) {
  const displayPath = endpoint.path || endpoint.endpointUrl;
  const displayText = displayPath || endpoint.endpointDescription || 'Endpoint';
  const canOpenEndpoint = Boolean(endpoint.endpointId && onOpenObject);
  const handleOpenEndpoint = () => {
    if (!endpoint.endpointId || !onOpenObject) return;
    onOpenObject({
      type: 'endpoint',
      id: endpoint.endpointId,
      label: displayText,
      endpointUrl: endpoint.endpointUrl,
    });
  };

  return (
    <Tooltip
      customContent={<EndpointTooltipContent endpoint={endpoint} />}
      positioning={{ placement: 'top' }}
      maxW="380px"
    >
      <HStack
        gap={1.5}
        alignItems="center"
        py={1}
        px={1.5}
        borderRadius="sm"
        cursor={canOpenEndpoint ? 'pointer' : 'default'}
        _hover={canOpenEndpoint ? { color: 'fg.default' } : undefined}
        onClick={canOpenEndpoint ? handleOpenEndpoint : undefined}
        width="100%"
      >
        {/* Rank indicator */}
        {endpoint.rank !== undefined && (
          <Text
            fontSize="2xs"
            color="fg.subtle"
            fontFamily="mono"
            fontWeight="semibold"
            minW="16px"
          >
            #{endpoint.rank}
          </Text>
        )}

        {/* Method badge */}
        {endpoint.method ? (
          <Box flexShrink={0}>{renderHttpMethodText(endpoint.method)}</Box>
        ) : null}

        {/* Path or description */}
        <HStack gap={1} alignItems="center" flex={1} minWidth={0}>
          {displayPath ? (
            <Link02Icon size={10} style={{ flexShrink: 0 }} />
          ) : (
            <Globe02Icon size={10} style={{ flexShrink: 0 }} />
          )}
          <Text
            fontSize="xs"
            fontFamily={displayPath ? 'mono' : undefined}
            color="fg.default"
            truncate
            flex={1}
          >
            {displayText}
          </Text>
        </HStack>

        {/* Score indicator */}
        {endpoint.score && (
          <Text
            fontSize="2xs"
            color="fg.muted"
            fontFamily="mono"
            flexShrink={0}
          >
            {endpoint.score}
          </Text>
        )}
      </HStack>
    </Tooltip>
  );
}

export function EndpointsRenderer({
  endpoints,
  maxHeight,
  onOpenObject,
}: EndpointsRendererProps) {
  return (
    <VStack alignItems="start" gap={1.5} width="100%">
      {/* Header */}
      <HStack alignItems="center" gap={2}>
        <Text fontSize="xs" fontWeight="medium" color="fg.default">
          {API_ENDPOINTS_LABEL}
        </Text>
        <Badge size="sm" variant="emphasis">
          {endpoints.length}
        </Badge>
      </HStack>

      {/* Compact list */}
      <VStack
        gap={0.5}
        width="100%"
        maxHeight={maxHeight}
        overflowY={maxHeight ? 'auto' : undefined}
        overflowX="hidden"
        alignItems="stretch"
        borderWidth="1px"
        borderColor="border.subtle"
        borderRadius="sm"
        p={1}
      >
        {endpoints.map((endpoint, index) => (
          <EndpointItem
            key={endpoint.endpointId || `endpoint-${index}`}
            endpoint={endpoint}
            onOpenObject={onOpenObject}
          />
        ))}
      </VStack>
    </VStack>
  );
}
